import React, { useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

// Replaces the old "paste a URL" text inputs across Create/Update Item,
// Create/Update Platter, and Mango forms. Uploads straight to the server's
// /upload route (which streams to Cloudinary) and hands back real URLs -
// no more requiring staff to already have the image hosted somewhere else.
//
// `multiple`: false = value/onChange deal in a single URL string.
//             true  = value/onChange deal in an array of URL strings.
const ImageUpload = ({ value, onChange, multiple = false, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const urls = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setError(null);
    setUploading(true);
    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      files.forEach((file) => formData.append("images", file));

      const response = await axios.post(`${API_BASE_URL}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedUrls = response.data.urls || [];
      if (multiple) {
        onChange([...urls, ...uploadedUrls]);
      } else {
        onChange(uploadedUrls[0] || "");
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      setError(
        err.response?.data?.message || "Upload failed. Check the file and try again."
      );
    } finally {
      setUploading(false);
      // Reset so selecting the exact same file again still fires onChange
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (index) => {
    if (multiple) {
      onChange(urls.filter((_, i) => i !== index));
    } else {
      onChange("");
    }
  };

  return (
    <div>
      <label className="block font-semibold mb-1 text-ink">{label}</label>

      {urls.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {urls.map((url, i) => (
            <div key={url + i} className="relative w-20 h-20">
              <img
                src={url}
                alt={`Upload ${i + 1}`}
                className="w-full h-full object-cover rounded border border-line"
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-600 text-white text-xs leading-none flex items-center justify-center"
                aria-label="Remove image"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={uploading}
        className="w-full text-sm text-sub file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-primary file:text-white file:font-semibold file:cursor-pointer disabled:opacity-60"
      />

      {uploading && <p className="text-xs text-sub mt-1">Uploading…</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

export default ImageUpload;
